import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import {
  Brain,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  ScrollText,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useSortableData } from "@/hooks/useSortableData";
import { useClientPaginatedData } from "@/hooks/usePaginatedData";
import {
  automationApi,
  AIEngagementPolicy,
  AIEngagementLog,
} from "@/services/api/automation";

export const Route = createFileRoute("/portal/automation/policies")({
  component: AIPolicies,
});

function AIPolicies() {
  const { token } = useAuth();
  const { can } = usePermissions();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId;
  const canCreatePolicies = can("create", "automation");
  const canEditPolicies = can("edit", "automation");
  const canDeletePolicies = can("delete", "automation");
  const [policies, setPolicies] = useState<AIEngagementPolicy[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedPolicy, setSelectedPolicy] =
    useState<AIEngagementPolicy | null>(null);
  const [loading, setLoading] = useState(false);

  // Engagement logs state
  const [engagementLogs, setEngagementLogs] = useState<AIEngagementLog[]>([]);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsServiceFilter, setLogsServiceFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  // Sort + paginate policies
  const { sortedData: sortedPolicies, requestSort, getSortDirection } =
    useSortableData(policies, {
      initialSort: { key: "policy_name", direction: "asc" },
    });

  const {
    paginatedData,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
  } = useClientPaginatedData({
    data: sortedPolicies as unknown as Record<string, unknown>[],
    itemsPerPage: 25,
    searchFields: ["policy_name", "service_name", "entity_type", "mode"],
    searchTerm,
  });
  const displayPolicies = paginatedData as unknown as AIEngagementPolicy[];

  const [formData, setFormData] = useState<{
    policy_name: string;
    entity_type: AIEngagementPolicy["entity_type"];
    service_name: string;
    mode: AIEngagementPolicy["mode"];
    engagement_percentage: number;
    auto_increase_enabled: boolean;
    increase_interval_hours: number;
    increase_step_percentage: number;
    max_percentage: number;
    max_risk_level: AIEngagementPolicy["max_risk_level"];
    max_daily_engagements: number | undefined;
    max_hourly_engagements: number | undefined;
  }>({
    policy_name: "",
    entity_type: "ticket",
    service_name: "",
    mode: "disabled",
    engagement_percentage: 0,
    auto_increase_enabled: false,
    increase_interval_hours: 24,
    increase_step_percentage: 5,
    max_percentage: 100,
    max_risk_level: "medium",
    max_daily_engagements: undefined,
    max_hourly_engagements: undefined,
  });

  useEffect(() => {
    if (token) {
      loadPolicies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organisationId]);

  const loadPolicies = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await automationApi.aiControl.listPolicies(token);
      setPolicies(response);
    } catch (error) {
      console.error("Failed to load policies:", error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      policy_name: "",
      entity_type: "ticket",
      service_name: "",
      mode: "disabled",
      engagement_percentage: 0,
      auto_increase_enabled: false,
      increase_interval_hours: 24,
      increase_step_percentage: 5,
      max_percentage: 100,
      max_risk_level: "medium",
      max_daily_engagements: undefined,
      max_hourly_engagements: undefined,
    });
  };

  const populateFormFromPolicy = (policy: AIEngagementPolicy) => {
    setFormData({
      policy_name: policy.policy_name,
      entity_type: policy.entity_type,
      service_name: policy.service_name || "",
      mode: policy.mode,
      engagement_percentage: policy.engagement_percentage,
      auto_increase_enabled: policy.auto_increase_enabled,
      increase_interval_hours: policy.increase_interval_hours,
      increase_step_percentage: policy.increase_step_percentage,
      max_percentage: policy.max_percentage,
      max_risk_level: policy.max_risk_level,
      max_daily_engagements: policy.max_daily_engagements,
      max_hourly_engagements: policy.max_hourly_engagements,
    });
  };

  const handleEditPolicy = (policy: AIEngagementPolicy) => {
    populateFormFromPolicy(policy);
    setSelectedPolicy(policy);
  };

  const handleCreatePolicy = async () => {
    if (!token) return;
    try {
      await automationApi.aiControl.createPolicy(token, formData);
      setShowCreateDialog(false);
      resetFormData();
      loadPolicies();
    } catch (error) {
      console.error("Failed to create policy:", error);
    }
  };

  const handleUpdatePolicy = async () => {
    if (!token || !selectedPolicy) return;
    try {
      await automationApi.aiControl.updatePolicy(
        token,
        selectedPolicy.id,
        formData,
      );
      setSelectedPolicy(null);
      resetFormData();
      loadPolicies();
    } catch (error) {
      console.error("Failed to update policy:", error);
    }
  };

  const handleSavePolicy = async () => {
    if (selectedPolicy) {
      await handleUpdatePolicy();
    } else {
      await handleCreatePolicy();
    }
  };

  const handleDeletePolicy = async (policyId: string) => {
    if (!token) return;
    if (!confirm("Are you sure you want to delete this policy?")) return;
    try {
      await automationApi.aiControl.deletePolicy(token, policyId);
      loadPolicies();
    } catch (error) {
      console.error("Failed to delete policy:", error);
    }
  };

  const loadEngagementLogs = async (serviceName?: string) => {
    if (!token) return;
    setLoadingLogs(true);
    try {
      const params: { service_name?: string; limit?: string } = { limit: "50" };
      if (serviceName) {
        params.service_name = serviceName;
      }
      const logs = await automationApi.aiControl.getLogs(token, params);
      setEngagementLogs(logs);
    } catch (error) {
      console.error("Failed to load engagement logs:", error);
      // Show empty state
      setEngagementLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleViewLogs = (serviceName?: string) => {
    setLogsServiceFilter(serviceName || "");
    setShowLogsDialog(true);
    loadEngagementLogs(serviceName);
  };

  const getModeBadge = (mode: string) => {
    const modeConfig: Record<string, { label: string; className: string }> = {
      disabled: { label: "Disabled", className: "" },
      shadow: { label: "Shadow", className: "border-slate-200 text-slate-700" },
      review: { label: "Review", className: "" },
      auto_pilot: {
        label: "Auto Pilot",
        className: "bg-green-100 text-green-800",
      },
      emergency: { label: "Emergency", className: "bg-red-100 text-red-800" },
    };
    const config = modeConfig[mode] || modeConfig.disabled;
    return (
      <Badge variant="secondary" className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const getRiskBadge = (level: string) => {
    const riskConfig = {
      low: { icon: CheckCircle, color: "text-green-500" },
      medium: { icon: AlertTriangle, color: "text-yellow-500" },
      high: { icon: AlertTriangle, color: "text-orange-500" },
      critical: { icon: AlertTriangle, color: "text-red-500" },
    };
    const config =
      riskConfig[level as keyof typeof riskConfig] || riskConfig.medium;
    const Icon = config.icon;
    return (
      <div className={`flex items-center gap-1 ${config.color}`}>
        <Icon className="h-4 w-4" />
        <span className="capitalize">{level}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">AI Engagement Policies</h1>
          <p className="mt-2 text-lg text-slate-600">
            Configure how AI engages with different entities and services
          </p>
        </div>
        <div className="flex gap-2">
          {canCreatePolicies && (
            <Button className="rounded-full" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Policy
            </Button>
          )}
        </div>
      </div>

      {/* Policy Table */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search policies..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={() => handleViewLogs()}>
            <ScrollText className="h-4 w-4 mr-2" />
            View Logs
          </Button>
          <Button variant="outline" size="sm" onClick={loadPolicies}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : displayPolicies.length === 0 ? (
          <div className="text-center py-12">
            <Brain className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No policies match your search" : "No policies configured"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term."
                : canCreatePolicies
                  ? "Create your first AI engagement policy to start automating responses."
                  : "No AI engagement policies have been configured yet."}
            </p>
            {!searchTerm && canCreatePolicies && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Policy
              </Button>
            )}
          </div>
        ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead direction={getSortDirection("policy_name")} onSort={() => requestSort("policy_name")}>Policy Name</SortableTableHead>
              <SortableTableHead direction={getSortDirection("entity_type")} onSort={() => requestSort("entity_type")}>Entity Type</SortableTableHead>
              <SortableTableHead direction={getSortDirection("service_name")} onSort={() => requestSort("service_name")}>Service</SortableTableHead>
              <SortableTableHead direction={getSortDirection("mode")} onSort={() => requestSort("mode")}>Mode</SortableTableHead>
              <SortableTableHead direction={getSortDirection("engagement_percentage")} onSort={() => requestSort("engagement_percentage")}>Engagement</SortableTableHead>
              <SortableTableHead direction={null} onSort={() => {}} disabled>Auto-Increase</SortableTableHead>
              <SortableTableHead direction={getSortDirection("max_risk_level")} onSort={() => requestSort("max_risk_level")}>Max Risk</SortableTableHead>
              <SortableTableHead direction={null} onSort={() => {}} disabled>Limits</SortableTableHead>
              <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{" "}</SortableTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayPolicies.map((policy) => (
              <TableRow key={policy.id}>
                <TableCell className="font-medium">
                  {policy.policy_name}
                </TableCell>
                <TableCell>
                  <span className="capitalize">{policy.entity_type}</span>
                </TableCell>
                <TableCell>
                  <span className="text-muted-foreground">
                    {policy.service_name || "All Services"}
                  </span>
                </TableCell>
                <TableCell>{getModeBadge(policy.mode)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-secondary rounded-full h-2">
                      <div
                        className="bg-primary rounded-full h-2 transition-all"
                        style={{ width: `${policy.engagement_percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium min-w-[3ch]">
                      {policy.engagement_percentage}%
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {policy.auto_increase_enabled ? (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <TrendingUp className="h-3 w-3" />
                      <span>
                        +{policy.increase_step_percentage}% /{" "}
                        {policy.increase_interval_hours}h
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>{getRiskBadge(policy.max_risk_level)}</TableCell>
                <TableCell>
                  <div className="text-sm text-muted-foreground">
                    {policy.max_daily_engagements && (
                      <div>{policy.max_daily_engagements}/day</div>
                    )}
                    {policy.max_hourly_engagements && (
                      <div>{policy.max_hourly_engagements}/hr</div>
                    )}
                    {!policy.max_daily_engagements &&
                      !policy.max_hourly_engagements && <span>—</span>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canEditPolicies && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditPolicy(policy)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {canDeletePolicies && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePolicy(policy.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={totalItems}
              pageSize={itemsPerPage}
              pageSizeOptions={[10, 25, 50]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={currentPage > 1}
              canGoNext={currentPage < totalPages}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
              onFirstPage={() => setCurrentPage(1)}
              onLastPage={() => setCurrentPage(totalPages)}
              onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
              onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog
        open={showCreateDialog || !!selectedPolicy}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setSelectedPolicy(null);
            resetFormData();
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedPolicy ? "Edit Policy" : "Create AI Engagement Policy"}
            </DialogTitle>
            <DialogDescription>
              Configure how AI should engage with entities
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto px-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="policy-name">Policy Name</Label>
                <Input
                  id="policy-name"
                  placeholder="e.g., Customer Support Auto-Response"
                  value={formData.policy_name}
                  onChange={(e) =>
                    setFormData({ ...formData, policy_name: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="service">Service</Label>
                <Input
                  id="service"
                  placeholder="e.g., freshdesk (leave empty for all)"
                  value={formData.service_name}
                  onChange={(e) =>
                    setFormData({ ...formData, service_name: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="entity-type">Entity Type</Label>
                <Select
                  value={formData.entity_type}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      entity_type: value as AIEngagementPolicy["entity_type"],
                    })
                  }
                >
                  <SelectTrigger id="entity-type" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ticket">Ticket</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="universal">Universal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="mode">Engagement Mode</Label>
                <Select
                  value={formData.mode}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      mode: value as AIEngagementPolicy["mode"],
                    })
                  }
                >
                  <SelectTrigger id="mode" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disabled">Disabled</SelectItem>
                    <SelectItem value="shadow">Shadow Mode</SelectItem>
                    <SelectItem value="review">Review Required</SelectItem>
                    <SelectItem value="auto_pilot">Auto Pilot</SelectItem>
                    <SelectItem value="emergency">Emergency Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="engagement">
                Engagement Percentage: {formData.engagement_percentage}%
              </Label>
              <Slider
                id="engagement"
                min={0}
                max={100}
                step={5}
                value={[formData.engagement_percentage]}
                onValueChange={(value: number[]) =>
                  setFormData({ ...formData, engagement_percentage: value[0] })
                }
                className="mt-2"
              />
            </div>

            <div className="space-y-3 border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-increase">
                    Auto-Increase Engagement
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Gradually increase engagement percentage over time
                  </p>
                </div>
                <Switch
                  id="auto-increase"
                  checked={formData.auto_increase_enabled}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, auto_increase_enabled: checked })
                  }
                />
              </div>
              {formData.auto_increase_enabled && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="interval">Interval (hours)</Label>
                      <Input
                        id="interval"
                        type="number"
                        min="1"
                        value={formData.increase_interval_hours}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            increase_interval_hours: parseInt(e.target.value),
                          })
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="step">Step Increase (%)</Label>
                      <Input
                        id="step"
                        type="number"
                        min="1"
                        max="100"
                        value={formData.increase_step_percentage}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            increase_step_percentage: parseInt(e.target.value),
                          })
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="max">
                      Maximum Percentage: {formData.max_percentage}%
                    </Label>
                    <Slider
                      id="max"
                      min={0}
                      max={100}
                      step={5}
                      value={[formData.max_percentage]}
                      onValueChange={(value: number[]) =>
                        setFormData({ ...formData, max_percentage: value[0] })
                      }
                      className="mt-2"
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              <Label htmlFor="risk">Maximum Risk Level</Label>
              <Select
                value={formData.max_risk_level}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    max_risk_level:
                      value as AIEngagementPolicy["max_risk_level"],
                  })
                }
              >
                <SelectTrigger id="risk" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low Risk</SelectItem>
                  <SelectItem value="medium">Medium Risk</SelectItem>
                  <SelectItem value="high">High Risk</SelectItem>
                  <SelectItem value="critical">Critical Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="daily-limit">Daily Limit (optional)</Label>
                <Input
                  id="daily-limit"
                  type="number"
                  min="1"
                  placeholder="No limit"
                  value={formData.max_daily_engagements || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_daily_engagements: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="hourly-limit">Hourly Limit (optional)</Label>
                <Input
                  id="hourly-limit"
                  type="number"
                  min="1"
                  placeholder="No limit"
                  value={formData.max_hourly_engagements || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_hourly_engagements: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    })
                  }
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setSelectedPolicy(null);
                resetFormData();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSavePolicy}>
              {selectedPolicy ? "Update Policy" : "Create Policy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Engagement Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5" />
              AI Engagement Logs
            </DialogTitle>
            <DialogDescription>
              View recent AI engagement decisions for debugging
              {logsServiceFilter &&
                ` (filtered by service: ${logsServiceFilter})`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {loadingLogs ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : engagementLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <ScrollText className="h-12 w-12 mb-4" />
                <p>No engagement logs found</p>
                <p className="text-sm">
                  Logs will appear here once AI makes engagement decisions
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Risk</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Decision</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {engagementLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-mono">
                            {log.entity_id.slice(0, 8)}...
                          </span>
                          <div className="text-xs text-muted-foreground">
                            {log.entity_type}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{log.service_name}</TableCell>
                      <TableCell>{log.action_type}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            log.risk_level === "low"
                              ? "border-green-500 text-green-700"
                              : log.risk_level === "medium"
                                ? "border-yellow-500 text-yellow-700"
                                : log.risk_level === "high"
                                  ? "border-orange-500 text-orange-700"
                                  : "border-red-500 text-red-700"
                          }
                        >
                          {log.risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {(log.confidence_score * 100).toFixed(0)}%
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            log.decision === "approved"
                              ? "default"
                              : "secondary"
                          }
                          className={
                            log.decision === "approved"
                              ? "bg-green-100 text-green-800"
                              : ""
                          }
                        >
                          {log.decision}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogsDialog(false)}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => loadEngagementLogs(logsServiceFilter)}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
